<script lang="ts">
export default {
  name: 'AccountProgramList',
};

interface ProgramYearGroup {
  year: number;
  programs: Program[];
}
</script>

<script setup lang="ts">
import { ref, watch, computed, onMounted, inject } from 'vue';
import { CircleUser, Plus, Pencil, EllipsisVertical, Files, Copy, Trash2, LayoutList, Calendar } from 'lucide-vue-next';
import { accountService } from '@/services/api';
import { Program } from '@/types/Programs';
import FilterBar from '@/components/FilterBar.vue';
import { compactMoney } from '@/utilities/Money';
import ConfirmModal from '@/components/ConfirmModal.vue';
import { IsUserAClient } from '@/utilities/AuthUtils';
import { AppState } from '@/store';
import { filters } from '@/utilities/Filters';
import { Store } from 'vuex';
import { useRouter, useRoute } from 'vue-router';
import Skeleton from '@/components/v2/Skeleton.vue';
import MenuDropdown, { type MenuItem } from '@/components/v2/MenuDropdown.vue';
import AppButton from '@/components/v2/AppButton.vue';
import Chip from '@/components/v2/Chip.vue';
import Toggle from '@/components/v2/Toggle.vue';
import EmptyDisplay from '@/components/EmptyDisplay.vue';
import emptySrc from '@/assets/images/empty.svg';

const $router = useRouter();
const $route = useRoute();

// -----
// data
// -----
const loading = ref(false);
const programs = ref<Program[]>([]);
const accountId = ref('');
const showCurrentOnly = ref(true);
const programsToMerge = ref<number[]>([]);
const deleteProgramId = ref<number | null>(null);
const showDeleteModal = ref(false);

const store = inject<Store<AppState>>('store');

// ---------
// watchers
// ---------
watch(showCurrentOnly, async () => {
  await loadPrograms();
});

// ---------
// computed
// ---------
const hasPrograms = computed(() => {
  return programs.value && programs.value.length > 0;
});

const yearGroupedPrograms = computed(() => {
  if (!hasPrograms.value) {
    return [];
  }
  return Object.values(programs.value.reduce((acc, curr) => {
    if (!acc[curr.year]) {
      acc[curr.year] = {year: curr.year, programs: []};
    }
    acc[curr.year].programs.push(curr);
    return acc;
  }, {} as Record<string, ProgramYearGroup>)).sort((a, b) => Number(b.year) - Number(a.year));
});

const isAClientAccount = computed(() => {
  return IsUserAClient(store?.state.user);
});

const sortedPrograms = computed(() => {
  const sortedPrograms = [...programs.value];
  return sortedPrograms.sort((a, b) => {
    if (a.year === b.year) {
      return a.name.localeCompare(b.name);
    }
    return b.year - a.year;
  });
});

// -----------
// lifecycles
// -----------
onMounted(async () => {
    accountId.value = $route.params.id as string;
    await loadPrograms();

    // if no current programs, then load expired
  if (programs.value.length === 0) {
    showCurrentOnly.value = false;
  }
});

// ----------
// functions
// ----------
function isProgramAppGenerated(program: Program): boolean {
  return program.type === 'Automated' || program.type === 'EPIC';
}

function toggleMergedProgram(programId: number) {
  if (programsToMerge.value.includes(programId)) {
    programsToMerge.value = programsToMerge.value.filter(id => id !== programId);
  } else {
    programsToMerge.value.push(programId);
  }
}

async function mergePrograms(): Promise<boolean> {
  await $router.push(`/account/${accountId.value}/programs/new?mergedPrograms=${programsToMerge.value.join(',')}`);
  return true;
}

async function deleteProgram(programID: number): Promise<boolean> {
  await accountService.deleteProgram(accountId.value, programID);
  await loadPrograms();
  return true;
}

async function loadPrograms(): Promise<void> {
  loading.value = true;
  programs.value = await accountService.getProgramsForAccount(accountId.value, !showCurrentOnly.value, false, true);
  loading.value = false;
}

function limitValue(program: Program): string | number {
  if (program.coverageTypes.length === 1 && program.coverageTypes[0] === 'Work Comp') {
    return 'Statutory';
  }
  return compactMoney(program.maxLimitInclAttachment) ?? '--';
}

function getProgramMenuItems(program: Program): MenuItem[] {
  const items: MenuItem[] = [];
  items.push({
    label: 'Open',
    handler: () => $router.push(`/account/${accountId.value}/programs/${program.id}`),
    icon: Files,
  });
  if (!isProgramAppGenerated(program) && !isAClientAccount.value) {
    items.push({
      label: 'Edit',
      handler: () => $router.push(`/account/${accountId.value}/programs/${program.id}/edit`),
      icon: Pencil,
    });
  }
  if (!isAClientAccount.value) {
    items.push({
      label: 'Copy',
      handler: () => $router.push(`/account/${accountId.value}/programs/new?copiedProgramID=${program.id}`),
      icon: Copy,
    });
    items.push({
      label: 'Delete',
      handler: () => {
        deleteProgramId.value = program.id;
        showDeleteModal.value = true;
      },
      icon: Trash2,
      iconColorClass: 'tw:text-error',
      textColorClass: 'tw:text-error',
    });
  }
  return items;
}

async function handleDeleteProgram(): Promise<boolean> {
  if (deleteProgramId.value !== null) {
    await deleteProgram(deleteProgramId.value);
    deleteProgramId.value = null;
    showDeleteModal.value = false;
  }
  return true;
}
</script>

<template>
  <div>
    <FilterBar class="filter-bar" v-if="!loading">
      <div class="tw:flex tw:w-full tw:justify-between">
        <div class="tw:flex">
          <RouterLink
            v-if="!isAClientAccount"
            class="tw:btn tw:btn-sm tw:btn-primary tw:rounded-full tw:justify-start"
            data-testid="create-program"
            :to="`/account/${accountId}/programs/new`">
            Create New Program<Plus :size="16" class="ml-1" />
          </RouterLink>
          
          <ConfirmModal
            v-if="hasPrograms"
            title="Select Programs to Merge"
            :action="mergePrograms"
            :dismiss="() => programsToMerge = []"
            confirmText="Merge"
            size="md"
            :is-confirm-disabled="() => programsToMerge.length < 2"
          >
            <template v-slot:button="{ props }">
              <slot name="button" :props="props">
                <AppButton
                  v-if="!isAClientAccount"
                  primary
                  class="tw:justify-start tw:ml-4 tw:rounded-full"
                  data-testid="merge-programs"
                  v-bind="props">
                  Create From Existing Programs
                </AppButton>
              </slot>
            </template>
            <div class='tw:flex tw:flex-row'>
              <ul class='tw:list-none tw:space-y-2 tw:pl-4 tw:w-full'>
                <li v-for='program in sortedPrograms' :key="program.id"
                  class='tw:flex tw:flex-row tw:grow tw:items-center tw:cursor-pointer tw:text-sm tw:text-black/70
                    tw:rounded tw:p-1 tw:hover:bg-gray-100'
                  @click='toggleMergedProgram(program.id)'
                  :data-testid="`merge-select-${program.id}`"
                >
                  <input type='checkbox' class="tw:cursor-pointer tw:scale-125" :checked="programsToMerge.includes(program.id)"/>
                  <span class='tw:ml-4 tw:text-base'>{{ program.name }}</span>
                </li>
              </ul>
            </div>
          </ConfirmModal>
        </div>
        <toggle id="show-current" v-model="showCurrentOnly" label="Show Current Only"/>
      </div>
    </FilterBar>

    <div class="content">
      <h1 class="tw:my-2">Account Program Details</h1>

      <div class="loader inner" v-if="loading">
        <Skeleton v-for="i in 4" :key="i" shape="rectangle" width="100%" height="120px" class="tw:rounded-md" />
      </div>

      <div class="card-groups inner" v-else-if="!loading && hasPrograms">
        <div class="card-programs" v-for="(programGroup, index) in yearGroupedPrograms" :key="index">

          <h2 class="tw:mb-4 tw:text-2xl tw:border-0 tw:border-b tw:border-solid tw:border-gray-200">
            {{ programGroup.year }}
          </h2>

          <div class="cards">
            <div v-for="(program, pIndex) in programGroup.programs" :key="pIndex"
                 class="card tw:border tw:border-solid tw:border-gray-400 tw:rounded"
            >
              <div class="card-header">
                <div class="card-header-title">
                  <div class="program-name">{{program.name}}</div>
                </div>
                <div class="card-header-actions">
                  <RouterLink
                    class="tw:btn tw:btn-sm tw:btn-outline tw:btn-secondary"
                    :to="`/account/${accountId}/programs/${program.id}`"
                    :data-testid="`open-program-${program.id}`">
                    Open
                  </RouterLink>
                  <MenuDropdown :items="getProgramMenuItems(program)" left>
                    <template #action>
                      <label tabindex="0" class="tw:btn tw:btn-ghost tw:btn-sm tw:btn-square" :data-testid="`${program.id}-actions-menu`">
                        <EllipsisVertical :size="20" />
                      </label>
                    </template>
                  </MenuDropdown>
                </div>
              </div>
              <div class="card-body">
                <div v-if="!isAClientAccount" class="bullets">
                  <div class="bullet program-type">
                    <LayoutList :size="16" class="tw:text-gray-500" />
                    <span class="tw:text-sm tw:text-black/70" :data-testid="`${program.id}-program-type`">{{ program.type }}</span>
                  </div>
                  <div class="bullet inserted-by" v-if="!isProgramAppGenerated(program)">
                    <CircleUser :size="16" class="tw:text-gray-500" />
                    <span class="tw:text-sm tw:text-black/70 text-info-darken-3" :data-testid="`${program.id}-inserted-by`">{{ program.createdBy }}</span>
                  </div>
                  <div class="bullet last-updated" v-if="!isProgramAppGenerated(program)">
                    <Calendar :size="16" class="tw:text-gray-500" />
                    <span class="tw:text-sm tw:text-black/70" :data-testid="`${program.id}-last-updated`">Updated on {{ filters.date(program.lastUpdatedTime) }}</span>
                  </div>
                </div>
                <div >
                  <Chip
                    v-for="coverage in program.coverageTypes"
                    :key="coverage"
                    class="tw:mx-1"
                    color-class="tw:bg-[#4E738A] tw:text-white"
                    size="sm"
                  >
                    {{ coverage }}
                  </Chip>
                </div>
              </div>
              <div class="card-footer tw:bg-gray-100 tw:border-0 tw:border-t tw:border-solid tw:border-gray-200">
                <div class="stat">
                  <div class="stat-label">Policies</div>
                  <div class="stat-value">{{program.policyCount}}</div>
                </div>
                <div class="stat">
                  <div class="stat-label">Limit</div>
                  <div class="stat-value">{{ limitValue(program) }}</div>
                </div>
                <div class="stat">
                  <div class="stat-label">Premium</div>
                  <div class="stat-value">{{compactMoney(program.totalPremiumSum) ?? '--'}}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-else>
        <EmptyDisplay :src="emptySrc" alt="No programs found" title="No programs found"
                      v-if="!hasPrograms"
                      text="No programs are currently available for this client."/>

      </div>
    </div>

    <ConfirmModal
      :show="showDeleteModal"
      title="Delete Program"
      :action="handleDeleteProgram"
      confirmText="Delete"
      size="lg"
      :dismiss="() => { showDeleteModal = false; deleteProgramId = null; }">
      <span>Are you sure you want to delete this program?</span>
    </ConfirmModal>
  </div>
</template>

<style scoped>
.content {
  margin: 1em;
}

.card-groups {
  display: grid;
  grid-template-columns: 1fr;
}

.card-programs {
  margin: 2rem 0 1rem;
}

.card-programs:first-child {
  margin: 0;
}

.cards,
.loader {
  display: grid;
  grid-gap: 2rem;
  grid-row-gap: 2rem;
  padding-bottom: 1rem;
  grid-template-columns: repeat(4, 1fr);
}

@media (max-width: 1150px) {
  .cards,
  .loader {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (max-width: 900px) {
  .cards,
  .loader {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 600px) {
  .cards,
  .loader {
    grid-template-columns: 1fr;
  }
}

.card {
  display: grid;
  grid-template-rows: repeat(3, auto);
}

.card:hover {
  box-shadow: 2px 2px 2px var(--v-border-base);
}

.card > .card-header {
  display: grid;
  grid-template-columns: 1fr auto;
  grid-column-gap: 0.5rem;
  padding: 1rem 1rem 0 1rem;
}

.card > .card-header > .card-header-title {
  display: grid;
  grid-template-columns: auto auto 1fr;
  grid-column-gap: 0.25rem;
  align-items: baseline;
  align-content: baseline;
}

.card > .card-header > .card-header-title > .program-name {
  font-size: 1.2rem;
  font-weight: 700;
  line-height: 1.1;
}

.card > .card-header > .card-header-actions {
  display: grid;
  grid-template-columns: 1fr auto;
  grid-column-gap: 0.25rem;
  align-items: center;
  align-content: center;
}

.card > .card-body {
  padding: 1rem 1rem 1.5rem 1rem;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  position: relative;
}

.card > .card-body > .bullets > .bullet {
  display: grid;
  grid-template-columns: auto 1fr;
  grid-column-gap: 0.25rem;
  align-content: center;
  align-items: center;
}

.card > .card-footer {
  padding: 0.5rem 1rem;
  border-radius: 0 0 4px 4px;
  grid-template-columns: repeat(3, auto);
  display: grid;
  grid-column-gap: 1rem;
  align-items: center;
  align-content: center;
}

.card > .card-footer > .stat {
  text-align: center;
}

.card > .card-footer > .stat > .stat-label {
  font-size: 0.6rem;
  text-transform: uppercase;
}

.card > .card-footer > .stat > .stat-value {
  font-size: 1.1rem;
  font-weight: 700;
}

.card-body > .coverage-chips {
  max-width: 50%;
}
</style>
